import axios from 'axios';
import { useBoundStore } from '@Store/index.ts';
import { EnvironmentVariables, getEnvironmentVariable } from '../env/environment.service.ts';
import { handleServerError } from '../errors/error.service.ts';
import type { AxiosRequestConfig } from 'axios';
import type { ServerErrorResponse } from '@Types/types.ts';

declare module 'axios' {
  export interface AxiosRequestConfig {
    __retryCount?: number;
    __retryAttempt?: number;
  }
}

export const HttpService = axios.create({
  withCredentials: true,
  baseURL: getEnvironmentVariable(EnvironmentVariables.VITE_BASE_URL),
});

HttpService.interceptors.request.use((config) => {
  const captchaKey = window.sessionStorage.getItem('captchaToken');
  const accessToken = useBoundStore.getState().customer.accessToken;
  const isEmbedded = useBoundStore.getState().onboardingSlice.getIsEmbedded();

  if (captchaKey) {
    config.headers['recaptcha'] = captchaKey;
  }
  if (accessToken) {
    if (isEmbedded) {
      config.headers['Authorization'] = `lili ${accessToken}`;
    } else {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
  }
  return config;
});

HttpService.interceptors.response.use(
  // Any status code that lie within the range of 2xx cause this function to trigger
  function (response) {
    // We sometimes get the stateToken in the accessToken field, so if we have a stateToken, we don't want to set the accessToken.
    // This is a workaround for a bug in the backend that should be fixed.
    // If the user is required to authenticate, we need to clear the access token.
    if (
      response.data?.loginResponseCustomData?.stateToken ||
      response.data?.requireAuthentication
    ) {
      response.data.accessToken = '';
    }
    return response;
  },
  function (error: ServerErrorResponse & { config?: AxiosRequestConfig }) {
    const config = error?.config;
    const isNetworkOrTimeout = !error.response;
    const retryCount = config?.__retryCount ?? 2;
    const retryAttempt = config?.__retryAttempt ?? 0;

    if (isNetworkOrTimeout && retryCount > 0 && config) {
      config.__retryCount = retryCount - 1;
      config.__retryAttempt = retryAttempt + 1;
      const delay = Math.min(1000 * 2 ** retryAttempt, 5000);
      return new Promise((resolve) => setTimeout(resolve, delay)).then(() => HttpService(config));
    }

    const rawHttpStatus = error?.response?.status || 0;
    const structuredErrorCode = error?.response?.data?.errors?.[0]?.code;
    const errorCode = structuredErrorCode || (rawHttpStatus === 401 ? rawHttpStatus : 0);
    const localizedErrorMessage = error?.response?.data?.errors?.[0]?.localizedMessage;
    handleServerError(errorCode, localizedErrorMessage, rawHttpStatus);
    return Promise.reject(error);
  }
);
